/**
 * usePartyBillMasters — shared dropdown/master data loader for Party Bill module screens
 * (PartyBillScreen, PurchaseInvoiceAgstPBScreen, PartyBillReturnsScreen, GRCAgstPBScreen, PendingPBCancelScreen)
 * Screen family: 4.21.x
 */
import { useState, useEffect } from 'react';
import { API_BASE as API_URL, getToken } from '../../../lib/api';

export const tkn = () => getToken() || '';
export const hdrs = () => ({ Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' });
export const fmtN = (v) => (parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
export const autoSelect = (e) => e.target.select();
export const today = () => new Date().toISOString().split('T')[0];

export const usePartyBillMasters = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    const load = async (path, setter) => {
      try {
        const r = await fetch(`${API_URL}${path}`, { headers: hdrs() });
        const j = await r.json();
        if (j.success) setter(j.data || []);
      } catch { /* silent — dropdown stays empty */ }
    };
    load('/api/masters/suppliers', setSuppliers);
    load('/api/vouchers/accounts/all', setAccounts);
    load('/api/UOMMaster/api/v1/GetDetails', setUoms);
    load('/api/Division/api/V1/GetAllDivisions', setDivisions);
    load('/api/CategoryMaster/api/V1/GetAllCategories', setCategories);
    load('/api/BrandMaster/api/V1/GetAllBrands', setBrands);
  }, []);

  return { suppliers, accounts, uoms, divisions, categories, brands };
};

// ── In-session Party Bill store ──────────────────────────────────────────────
// Backend persistence for this module lands in the next phase (design-first, per
// user direction). Until then, bills saved from PartyBillScreen live here so the
// downstream screens (Purchase Invoice/Returns/GRC/Cancel Against PB) can look
// them up and demo the full flow end-to-end within a browser session.
let _sessionPartyBills = [];
export const addSessionPartyBill = (pb) => { _sessionPartyBills = [pb, ..._sessionPartyBills.filter(p => p.pbNo !== pb.pbNo)]; };
export const updateSessionPartyBill = (pbNo, patch) => { _sessionPartyBills = _sessionPartyBills.map(p => p.pbNo === pbNo ? { ...p, ...patch } : p); };
export const getSessionPartyBills = () => _sessionPartyBills;

// Party Bill list source for lookups/grids — tries the real backend first (future
// phase), falling back to the in-session store so the screens stay fully clickable.
export const fetchPartyBills = async ({ search = '', page = 1, pageSize = 10 } = {}) => {
  try {
    const r = await fetch(`${API_URL}/api/purchase/party-bills?search=${encodeURIComponent(search)}&page=${page}&pageSize=${pageSize}`, { headers: hdrs() });
    const j = await r.json();
    if (j.success && (j.data || []).length) return { data: j.data || [], total: j.total ?? (j.data || []).length };
  } catch { /* endpoint not wired yet */ }
  let f = _sessionPartyBills;
  if (search) { const s = search.toLowerCase(); f = f.filter(p => Object.values(p).some(v => String(v || '').toLowerCase().includes(s))); }
  return { data: f.slice((page - 1) * pageSize, page * pageSize), total: f.length };
};

export const SUPPLIER_COLUMNS = [
  { field: 'supplierName', header: 'SUPPLIER NAME' },
  { field: 'supplierId', header: 'SUPPLIER ID' },
  { field: 'gstin', header: 'GSTIN' },
  { field: 'city', header: 'CITY' },
  { field: 'phone', header: 'PHONE' },
];

export const PARTY_BILL_COLUMNS = [
  { field: 'pbNo', header: 'PB NO' },
  { field: 'pbDate', header: 'PB DATE' },
  { field: 'supplierName', header: 'SUPPLIER' },
  { field: 'pbTotal', header: 'PB TOTAL' },
  { field: 'status', header: 'STATUS' },
];
